import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Avatar from "@mui/material/Avatar";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import BaseButton from "../../../components/ui/BaseButton";
import BaseTooltip from "../../../components/ui/BaseTooltip";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import { showToast } from "../../../store/toast/toastSlice";
import {
  acceptCustomerCounterOffer,
  declineCustomerCounterOffer,
  fetchCustomerCounterOffer,
} from "../../../services/api/offers";
import { fetchWorkOrderPaymetDetails } from "../../../services/api/workOrder";
import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";
import { CurrencySymbol, WorkOrderStatusType } from "../../../utils/enum";
import { formatExpenseLabel, navigateWithReload } from "../../../utils/helpers";
import {
  PAYMENT_TYPE_LABELS,
  RATE_FIELDS,
  isRateShown,
  estimateLabour,
  fmt,
  toCustomerRateField,
  toNum,
} from "../CustomerCounterOfferModal/customerRates";

dayjs.extend(relativeTime);

// Soft tinted pills: a dot plus a word, never a solid block of colour.
const OUTCOMES = {
  PENDING: {
    label: "Awaiting your response",
    pill: "bg-amber-50 text-amber-700",
    dot: "bg-amber-500",
  },
  ACCEPTED: { label: "Accepted", pill: "bg-green-50 text-green-700", dot: "bg-green-500" },
  REJECTED: { label: "Declined", pill: "bg-red-50 text-red-700", dot: "bg-red-500" },
  REPLACED: { label: "Replaced", pill: "bg-zinc-100 text-zinc-600", dot: "bg-zinc-400" },
  // Still pending, but the ticket left review before the customer answered.
  UNANSWERED: { label: "Not answered", pill: "bg-zinc-100 text-zinc-600", dot: "bg-zinc-400" },
  // The provider took it back before the customer answered.
  WITHDRAWN: { label: "Withdrawn", pill: "bg-zinc-100 text-zinc-600", dot: "bg-zinc-400" },
};

const fullName = (user) => [user?.firstName, user?.lastName].filter(Boolean).join(" ");
const initials = (user) =>
  `${user?.firstName?.[0] || ""}${user?.lastName?.[0] || ""}`.toUpperCase() || "?";

// The offer's history has a row for every send (PENDING) and every answer
// (ACCEPTED / REJECTED, or WITHDRAWN by the provider), oldest first. Each send becomes one proposal: closed by
// the answer that follows it, or "Replaced" when the client sent a newer one first.
const buildProposals = (offerHistory = []) => {
  const proposals = [];
  offerHistory.forEach((row) => {
    const latest = proposals[proposals.length - 1];
    if (row.status === "PENDING") {
      if (latest?.outcome === "PENDING") latest.outcome = "REPLACED";
      proposals.push({ sent: row, outcome: "PENDING" });
    } else if (latest?.outcome === "PENDING") {
      // An accepted proposal is stored as ASSIGNED; it shows as "Accepted".
      latest.outcome = row.status === "ASSIGNED" ? "ACCEPTED" : row.status;
      latest.answer = row;
    }
  });
  return proposals.reverse();
};

// Everything a card shows, worked out once from the proposal's history row.
const describeProposal = ({ proposal, fields, paymentType, expenses, payment }) => {
  const { sent } = proposal;
  const currency = CurrencySymbol[sent.customerCurrency || sent.currency] || "";
  // The row stores the work order's rates when it was sent (unprefixed) and the
  // proposed ones (customer*).
  const original = Object.fromEntries(fields.map(({ name }) => [name, sent[name]]));
  const proposed = Object.fromEntries(
    fields.map(({ name }) => [name, sent[toCustomerRateField(name)] ?? sent[name]]),
  );
  const originalLabour = estimateLabour(paymentType, original);
  const proposedLabour = estimateLabour(paymentType, proposed);
  const expensesTotal = expenses.reduce((sum, e) => sum + (toNum(e.amount) || 0), 0);
  const currentTotal = toNum(payment?.totalWorkOrderCost);
  const newTotal =
    currentTotal != null ? currentTotal - originalLabour + proposedLabour + expensesTotal : null;
  const rates = fields
    .filter((field) => isRateShown(field, original[field.name], proposed[field.name]))
    .map(({ name, label, isAmount }) => ({
      name,
      label,
      from: original[name],
      to: proposed[name],
      isChanged: toNum(proposed[name]) !== toNum(original[name]),
      format: (value) => (isAmount ? `${currency} ${fmt(value)}` : `${value ?? 0} hrs`),
    }));
  return {
    currency,
    rates,
    originalLabour,
    proposedLabour,
    currentTotal,
    newTotal,
    paymentTypeLabel: PAYMENT_TYPE_LABELS[paymentType] || "-",
  };
};

const TimeAgo = ({ date }) =>
  date ? (
    <BaseTooltip content={dayjs(date).format("MMM D, YYYY [at] h:mm A")}>
      <span className="cursor-default">{dayjs(date).fromNow()}</span>
    </BaseTooltip>
  ) : null;

const StatusPill = ({ outcome }) => {
  const style = OUTCOMES[outcome];
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs ${style.pill}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`}></span>
      {style.label}
    </span>
  );
};

const Chevron = ({ isExpanded, onClick }) => (
  <button
    type="button"
    aria-expanded={isExpanded}
    aria-label={isExpanded ? "Hide details" : "Show details"}
    onClick={onClick}
    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-zinc-500 transition hover:bg-zinc-100"
  >
    <i
      className={`fa-solid fa-chevron-down text-xs transition-transform ${
        isExpanded ? "rotate-180" : ""
      }`}
    ></i>
  </button>
);

// Who sent it, and the answer or what happened to it.
const OutcomeLine = ({ proposal }) => {
  const { sent, outcome, answer } = proposal;
  return (
    <div className="flex items-center gap-2 text-xs text-zinc-500">
      <Avatar sx={{ width: 22, height: 22, fontSize: 10, bgcolor: "#0F40AE" }}>
        {initials(sent.createdBy)}
      </Avatar>
      <span>
        From {fullName(sent.createdBy) || "your provider"}
        {answer && (
          <>
            {" · "}
            {
              { ACCEPTED: "accepted", REJECTED: "declined", WITHDRAWN: "withdrawn" }[outcome]
            } by {fullName(answer.createdBy) || (outcome === "WITHDRAWN" ? "your provider" : "you")}{" "}
            <TimeAgo date={answer.createdAt} />
          </>
        )}
        {outcome === "REPLACED" && " · replaced by a newer offer"}
        {outcome === "UNANSWERED" && " · closed when the work order left review"}
      </span>
    </div>
  );
};

const ExpenseList = ({ expenses, currency, title = "Proposed expenses" }) =>
  expenses.length > 0 && (
    <div>
      <p className="mb-2 text-xs uppercase tracking-wide text-zinc-500">{title}</p>
      <dl className="space-y-2.5">
        {expenses.map((expense) => (
          <div key={expense.id} className="flex items-baseline justify-between gap-4 text-sm">
            <dt className="text-zinc-500">{formatExpenseLabel(expense)}</dt>
            <dd className="text-dark">
              {currency} {fmt(expense.amount)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );

const PaymentEstimate = ({ info }) =>
  info.newTotal != null && (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-zinc-50 px-4 py-3">
      <span className="text-sm text-zinc-500">Estimated payment</span>
      <span className="flex items-baseline gap-2">
        {info.newTotal !== info.currentTotal && (
          <span className="text-xs text-zinc-400 line-through">
            {info.currency} {fmt(info.currentTotal)}
          </span>
        )}
        <span className="text-base font-normal text-dark">
          {info.currency} {fmt(info.newTotal)}
        </span>
      </span>
    </div>
  );

const Actions = ({ onAccept, onDecline }) => (
  <div className="flex w-full gap-2 sm:w-auto">
    <BaseButton
      variant="outlined"
      color="primary"
      className="flex-1 sm:flex-none py-[5px] px-[22px]"
      style={{ borderRadius: 9999 }}
      onClick={onDecline}
    >
      Decline
    </BaseButton>
    <BaseButton
      color="primary"
      className="flex-1 sm:flex-none py-[5px] px-[26px]"
      style={{ borderRadius: 9999 }}
      onClick={onAccept}
    >
      Accept
    </BaseButton>
  </div>
);

// One side of the comparison: the labour estimate and every rate, current or
// proposed. Each side also lists its expenses: the ones the customer added to the
// ticket on the current side, the ones that come with the proposal on the other.
const RatesPanel = ({
  title,
  info,
  labour,
  valueOf,
  isProposed,
  isExpanded,
  expenses = [],
  expensesTitle,
}) => (
  <div
    className={`rounded-xl p-4 sm:p-5 ${
      isProposed ? "bg-primary/5 ring-1 ring-primary/20" : "bg-zinc-50"
    }`}
  >
    <p
      className={`text-xs uppercase tracking-wide ${isProposed ? "text-primary" : "text-zinc-500"}`}
    >
      {title}
    </p>
    <p className="mt-1 text-xl font-normal tracking-tight text-dark">
      {info.currency} {fmt(labour)}
    </p>
    <p className="text-xs text-zinc-500">Estimated labour</p>
    {isExpanded && (
      <dl className="mt-4 space-y-2">
        {info.rates.map((rate) => (
          <div key={rate.name} className="flex items-baseline justify-between gap-3 text-sm">
            <dt className="text-zinc-500">{rate.label}</dt>
            <dd className={isProposed && rate.isChanged ? "font-normal text-primary" : "text-dark"}>
              {rate.format(valueOf(rate))}
            </dd>
          </div>
        ))}
      </dl>
    )}
    {isExpanded && expenses.length > 0 && (
      <div className={`mt-4 border-t pt-4 ${isProposed ? "border-primary/20" : "border-zinc-200"}`}>
        <ExpenseList expenses={expenses} currency={info.currency} title={expensesTitle} />
      </div>
    )}
  </div>
);

// A proposal laid out side by side: what the work order has now next to what the
// provider proposes. A pending (or unanswered) proposal starts open, an answered
// one collapsed to its two labour totals.
const ProposalCard = ({ item, onAccept, onDecline }) => {
  const { proposal, info, expenses, currentExpenses, isActionable } = item;
  const [isExpanded, setIsExpanded] = useState(
    proposal.outcome === "PENDING" || proposal.outcome === "UNANSWERED",
  );

  return (
    <article
      className={`rounded-2xl border bg-white p-5 sm:p-6 ${
        isActionable ? "border-primary/30 shadow-sm" : "border-zinc-200"
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h6 className="text-dark">Offer</h6>
          <StatusPill outcome={proposal.outcome} />
          <span className="text-xs text-zinc-500">{info.paymentTypeLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-zinc-100 px-2.5 py-1 text-xs text-zinc-600">
            <i className="fa-light fa-clock"></i>
            Sent <TimeAgo date={proposal.sent.createdAt} />
          </span>
          <Chevron isExpanded={isExpanded} onClick={() => setIsExpanded((open) => !open)} />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
        <RatesPanel
          title="Current"
          info={info}
          labour={info.originalLabour}
          valueOf={(rate) => rate.from}
          isExpanded={isExpanded}
          expenses={currentExpenses}
          expensesTitle="Current expenses"
        />
        <RatesPanel
          title="Proposed"
          info={info}
          labour={info.proposedLabour}
          valueOf={(rate) => rate.to}
          isProposed
          isExpanded={isExpanded}
          expenses={expenses}
        />
      </div>

      {isExpanded && isActionable && (
        <div className="mt-4">
          <PaymentEstimate info={info} />
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-zinc-100 pt-4">
        <OutcomeLine proposal={proposal} />
        {isActionable && <Actions onAccept={onAccept} onDecline={onDecline} />}
      </div>
    </article>
  );
};

// The customer's view of the counter offer a client sent on their ticket, shown to
// them as a proposal: only the latest one, accepted or declined right on its card
// while it awaits an answer.
const ProposalsTab = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { updateWorkOrder } = useUpdateWorkOrder();
  const workOrderDetail = useSelector((state) => state.workOrder.workOrderDetails);
  const paymentType = workOrderDetail?.paymentType;
  const fields = RATE_FIELDS[paymentType] || [];
  const [confirmAction, setConfirmAction] = useState(null);
  // The provider edited the proposal while the customer had it open: their answer
  // was refused, and they are asked to reload to see the latest version.
  const [isProposalChanged, setIsProposalChanged] = useState(false);

  const { data: customerCounterOfferData, isLoading } = useQuery({
    queryKey: ["customerCounterOffer", workOrderDetail?.id],
    queryFn: () => fetchCustomerCounterOffer(workOrderDetail.id),
    enabled: !!workOrderDetail?.id,
  });

  const counterOffer = customerCounterOfferData?.data;
  const isAwaitingApproval = workOrderDetail?.status === WorkOrderStatusType.PENDING_APPROVAL;
  const canRespond = counterOffer?.status === "PENDING" && isAwaitingApproval;

  // The current payment: the customer's own expenses for every card, and the
  // before/after estimate for the pending proposal.
  const { data: workOrderPaymentData } = useQuery({
    queryKey: ["workOrderPayment", workOrderDetail?.id],
    queryFn: () => fetchWorkOrderPaymetDetails(workOrderDetail.id),
    enabled: !!workOrderDetail?.id,
  });

  // Only the latest proposal is shown to the customer; earlier ones they already
  // answered, or that the provider replaced or withdrew, are not.
  const proposals = buildProposals(counterOffer?.offerHistory).slice(0, 1);
  const items = proposals.map((built, index) => {
    const proposal =
      index === 0 && built.outcome === "PENDING" && !isAwaitingApproval
        ? { ...built, outcome: "UNANSWERED" }
        : built;
    const isActionable = index === 0 && proposal.outcome === "PENDING" && canRespond;
    // Expenses hang off the offer itself, so they belong to its latest send; a
    // declined or withdrawn offer's proposed expenses were removed.
    const expenses =
      index === 0 && !["REJECTED", "WITHDRAWN"].includes(proposal.outcome)
        ? counterOffer?.expenses || []
        : [];
    return {
      proposal,
      isActionable,
      expenses,
      // The expenses the customer put on the ticket themselves.
      currentExpenses: workOrderPaymentData?.data?.clientRates?.expenses || [],
      info: describeProposal({
        proposal,
        fields,
        paymentType,
        expenses,
        payment: isActionable ? workOrderPaymentData?.data?.clientRates : null,
      }),
    };
  });

  const onResponded = async (message) => {
    setConfirmAction(null);
    dispatch(showToast({ message, type: "success" }));
    queryClient.invalidateQueries({ queryKey: ["customerCounterOffer"] });
    queryClient.invalidateQueries({ queryKey: ["workOrderPayment"] });
    await updateWorkOrder();
  };
  const onError = (error) => {
    if (
      error?.response?.status === 409 &&
      error?.response?.data?.code === "COUNTER_OFFER_CHANGED"
    ) {
      setConfirmAction(null);
      setIsProposalChanged(true);
      return;
    }
    dispatch(
      showToast({
        message: error?.response?.data?.message || "Something went wrong",
        type: "error",
      }),
    );
  };

  const { isPending: isAccepting, mutateAsync: acceptMutation } = useMutation({
    mutationFn: acceptCustomerCounterOffer,
    onSuccess: () => onResponded("Offer Accepted Successfully"),
    onError,
  });
  const { isPending: isDeclining, mutateAsync: declineMutation } = useMutation({
    mutationFn: declineCustomerCounterOffer,
    onSuccess: () => onResponded("Offer Declined"),
    onError,
  });

  if (isLoading) {
    return (
      <div className="space-y-4 py-4">
        {[0, 1].map((key) => (
          <div key={key} className="h-40 animate-pulse rounded-2xl bg-zinc-100"></div>
        ))}
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="py-4">
        <div className="rounded-2xl border border-dashed border-zinc-300 bg-white px-6 py-12 text-center">
          <p className="text-dark">No offers yet</p>
          <p className="mt-1 text-sm text-zinc-500">
            When your provider offers different rates, they will show up here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 py-4">
      {items.map((item) => (
        <ProposalCard
          key={item.proposal.sent.id}
          item={item}
          onAccept={() => setConfirmAction("ACCEPT")}
          onDecline={() => setConfirmAction("DECLINE")}
        />
      ))}

      {confirmAction && (
        <BaseConfirmModal
          open={!!confirmAction}
          onClose={() => setConfirmAction(null)}
          // onError has already shown the toast; don't let the rejection escape too.
          onConfirm={() =>
            (confirmAction === "ACCEPT" ? acceptMutation : declineMutation)({
              offerId: counterOffer.id,
              // The version on screen; the server refuses if the provider changed it.
              updatedAt: counterOffer.updatedAt,
            }).catch(() => {})
          }
          isLoading={isAccepting || isDeclining}
          title={confirmAction === "ACCEPT" ? "Accept Offer" : "Decline Offer"}
          description={
            <p>
              {confirmAction === "ACCEPT"
                ? "Your work order's rates will be updated to the offered rates, and any offered expenses will be added. This can't be undone."
                : "Your work order keeps its current rates, and the offered expenses will not be added. This can't be undone."}
            </p>
          }
          confirmText={confirmAction === "ACCEPT" ? "Accept" : "Decline"}
          type={confirmAction === "ACCEPT" ? "confirm" : "warning"}
        />
      )}

      {isProposalChanged && (
        <BaseConfirmModal
          open={isProposalChanged}
          onClose={() => setIsProposalChanged(false)}
          onConfirm={() =>
            navigateWithReload(`${window.location.pathname}${window.location.search}`)
          }
          title="Offer Updated"
          description={
            <p>
              Your provider has updated this offer since you opened it. Please refresh the page to
              review the latest offer before you respond.
            </p>
          }
          confirmText="Refresh"
          type="warning"
        />
      )}
    </div>
  );
};

export default ProposalsTab;
